// Phase 7 — Banner admin screen, migrated to shadcn/Tailwind.
//
// UI only: antd Drawer/Form/Upload/DatePicker/Modal/Select/Tooltip/Empty + SpinLoader
// → shadcn Sheet + Select + the shared DateRangePicker + ui/table + Tooltip +
// EmptyState + ConfirmDialog + LoadingOverlay, with an inline image dropzone. Every
// dispatch (getBannerMetaAction, getBannerAttachmentAction, createOrUpdateBannerAction)
// and the AWS upload wiring are unchanged. The antd Form is dropped — the fields are
// controlled by component state (imgList/bannerSortOrder/imageObj); the antd
// validators are ported one-to-one into a local validate() gate.
import { useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import { DateRange } from 'react-day-picker'
import { Calendar, FileText, Image as ImageIcon, Pencil, Plus, Search, Trash2, UploadCloud, X } from 'lucide-react'
import { toast } from 'sonner'
import { deleteFileFromS3, uploadPhotoToAWS } from '../Components/SubComponents/awsUpload'
import { isArrayNotEmpty, toMb } from '../Utils/helpers'
import { createOrUpdateBannerAction, getBannerAttachmentAction, getBannerMetaAction } from '../Store/actions/apparelAction'
import { useDispatch, useSelector } from 'react-redux'
import AppConstant from '../Globals/AppConstants'
import ApparelMenu from '../Components/SubComponents/apparelMenu'
import { LoadingOverlay } from '../shared/loading-overlay'
import { EmptyState } from '../shared/empty-state'
import { ConfirmDialog } from '../shared/confirm-dialog'
import { DateRangePicker } from '../Components/Reports/DateRangePicker'
import { Button } from 'ui/button'
import { Input } from 'ui/input'
import { Label } from 'ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'ui/select'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from 'ui/sheet'
import { Card, CardContent } from 'ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'ui/table'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'ui/tooltip'
import { cn } from 'lib/utils'

const IMG_BASE = `${process.env.REACT_APP_AWS_CLOUD_FRONT_URL}/${process.env.REACT_APP_IMAGE_DESTINATION_BUCKET}banner/images/`;

const Banner = () => {
  const dispatch = useDispatch();
  const [page, setPage] = useState<number>(1);
  const [subPage, setSubPage] = useState<number>(0);
  const [open, setOpen] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [imgList, setImgList] = useState<any[]>([]);
  const [singleImg, setSingleImg] = useState<Record<string, any> | null>(null);
  const [isEdit, setEdit] = useState<boolean>(false);
  const [imageObj, setImageObj] = useState({
    entityTypeId: AppConstant.FIVE,
    entityId: subPage,
    startDate: '',
    endDate: ''
  })
  const [bannerUpload, setBannerUpload] = useState<boolean>(false)
  const [bannerId, setBannerId] = useState<any>(null);
  const [bannerSortOrder, setBannerSortOrder] = useState<number>(1);
  const [rangeValue, setRangeValue] = useState<DateRange | undefined>(undefined);
  const [imageError, setImageError] = useState<string>("");
  const [rangeError, setRangeError] = useState<string>("");

  const apparelReducerState = useSelector((state: any) => state.ApparelReducerState)
  const { getBannerAttachmentLoad, getMetaBannerData,
    getBannerAttachmentData, createOrUpdateBannerLoad, createOrUpdateBannerData } = apparelReducerState;

  const getBannerMetaData = getMetaBannerData?.getBannerMeta;
  const getBannerAttachmentList = getBannerAttachmentData?.getBannerAttachment;

  const createOrUpdateBannerApi = (files: any, isDeleted: any) => {
    let obj = {
      fileName: files?.file?.name ? files?.file?.name : imgList[0]?.fileName,
      fileUuid: files?.fileUuid ? files?.fileUuid : imgList[0]?.fileUuid,
      entityTypeId: imageObj?.entityTypeId,
      sortOrder: bannerSortOrder,
      entityId: subPage,
      mimeType: files?.mimeType ? files?.mimeType : files?.file?.name?.substring(files?.file?.name?.lastIndexOf('.')),
      fileSize: typeof files?.fileSize == "string" ? 0 : toMb(files?.file?.size),
      startDate: files?.startDate ? moment(files?.startDate).format("YYYY-MM-DD") : imageObj?.startDate,
      endDate: files?.startDate ? moment(files?.endDate).format("YYYY-MM-DD") : imageObj?.endDate,
      isDeleted: isDeleted
    }
    let payload = {
      attachment: [obj]
    }
    dispatch(createOrUpdateBannerAction(payload))
    setBannerUpload(true)
  }

  const getBannerAttachmentListApi = (entityId: any) => {
    let payload = {
      entityId: entityId ? entityId : subPage
    }
    dispatch(getBannerAttachmentAction(payload))
  }

  let pageArray = [
    { id: 1, title: "Home Page" }
  ]

  const showModal = (data: any) => {
    setBannerId(data?.bannerId);
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (isArrayNotEmpty(getBannerAttachmentList) && open && !isEdit) {
      setBannerSortOrder(Number(getBannerAttachmentList?.length) + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getBannerAttachmentList, bannerSortOrder, open, isEdit])

  const showDrawer = (value: any) => {
    setOpen(true);
    if (value) {
      setEdit(true);
      setImageObj({
        ...imageObj,
        startDate: moment(value?.startDate).format("YYYY-MM-DD"),
        endDate: moment(value?.endDate).format("YYYY-MM-DD")
      })
      setBannerSortOrder(value?.sortOrder ? value?.sortOrder : 1);
      setRangeValue({ from: new Date(value?.startDate), to: new Date(value?.endDate) })
      setImgList([value])
    }
  };

  const deleteData = (value: any) => {
    setImageObj({
      ...imageObj,
      startDate: moment(value?.startDate).format("YYYY-MM-DD"),
      endDate: moment(value?.endDate).format("YYYY-MM-DD")
    })
    createOrUpdateBannerApi(value, 1)
    setIsModalOpen(false)
  }

  const onClose = () => {
    setOpen(false);
    setEdit(false);
    setSingleImg(null);
    setImgList([]);
    setImageObj({
      ...imageObj,
      startDate: '',
      endDate: ''
    })
    setRangeValue(undefined)
    setImageError("");
    setRangeError("");
  };

  useEffect(() => {
    dispatch(getBannerMetaAction(""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isArrayNotEmpty(getBannerMetaData)) {
      setSubPage(getBannerMetaData[0]?.id)
      getBannerAttachmentListApi(getBannerMetaData[0]?.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getBannerMetaData]);

  useEffect(() => {
    if (!createOrUpdateBannerLoad && bannerUpload)
      toast.success(createOrUpdateBannerData?.createOrUpdateBanner);
    getBannerAttachmentListApi(subPage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOrUpdateBannerLoad, bannerUpload])

  const uploadAttachmentAws = (type: number) => {
    if (singleImg)
      uploadPhotoToAWS(singleImg, createOrUpdateBannerApi, type)
    else
      createOrUpdateBannerApi('', 0);
  }

  // Ported from the antd Upload customRequest (count + extension guard).
  const onPickImage = (file: File) => {
    if (imgList?.length < (isEdit ? 1 : 4)) {
      let imageType = file?.name?.split('.');
      let extension = imageType?.[imageType.length - 1]?.toLowerCase();
      if (['png', 'jpg', 'jpeg'].includes(extension)) {
        let obj = {
          previewImage: URL.createObjectURL(file),
          file: file
        }
        setImgList((prev: any) => prev ? [...prev, obj] : [obj])
        setSingleImg(obj);
      } else {
        toast.warning('Please Select .png or .jpg or .jpeg Images Only');
      }
    } else {
      toast.error(`Please Select ${isEdit ? " 1 image" : " 4 Images"}  Only`);
    }
  }

  const setImages = (data: any) => {
    setImgList(imgList?.filter((item: any) => data?.file?.uid != item?.file?.uid))
    if (data?.fileUuid) {
      deleteFileFromS3(data?.fileUuid, 5)
      createOrUpdateBannerApi(data, 1)
    }
  }

  const handleRangeChange = (range: DateRange | undefined) => {
    setRangeValue(range)
    setImageObj({
      ...imageObj,
      startDate: range?.from ? moment(range.from).format('YYYY-MM-DD') : '',
      endDate: range?.to ? moment(range.to).format('YYYY-MM-DD') : ''
    })
  };

  // Ported from the antd Form.Item validators (image + range required).
  const validate = () => {
    let ok = true;
    if (imgList.length === 0) {
      setImageError("Please upload your Image!");
      ok = false;
    } else {
      setImageError("");
    }
    if (!imageObj?.startDate || !imageObj?.endDate) {
      setRangeError("Please input your start date and end date");
      ok = false;
    } else {
      setRangeError("");
    }
    return ok;
  }

  const uploadBannerSubmit = () => {
    if (!validate()) return;
    onClose()
    uploadAttachmentAws(5)
  }

  const subPageAction = (entityId: any) => {
    getBannerAttachmentListApi(entityId)
    setSubPage(entityId)
  }

  const deleteBannerData = (getBannerAttachmentList || [])?.find((x: any) => x?.bannerId == bannerId);

  // Presentation-only search/sort over the already-fetched list — no new
  // dispatch, `getBannerAttachmentListApi` payload is unchanged.
  const [imageSearch, setImageSearch] = useState("");
  const [imageSort, setImageSort] = useState<"sortOrder" | "name" | "date">("sortOrder");
  const displayedBannerList = useMemo(() => {
    const list = (getBannerAttachmentList || []).filter((v: any) =>
      !imageSearch || (v?.fileName || "").toLowerCase().includes(imageSearch.toLowerCase())
    );
    const sorted = [...list];
    if (imageSort === "name") sorted.sort((a: any, b: any) => (a?.fileName || "").localeCompare(b?.fileName || ""));
    else if (imageSort === "date") sorted.sort((a: any, b: any) => new Date(a?.startDate).getTime() - new Date(b?.startDate).getTime());
    else sorted.sort((a: any, b: any) => (a?.sortOrder || 0) - (b?.sortOrder || 0));
    return sorted;
  }, [getBannerAttachmentList, imageSearch, imageSort]);

  return (
    <div className="mz h-full overflow-y-auto px-6 py-4">
      <ApparelMenu selectedKey="10" />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[220px_1fr]">
        {/* Pages + Sub pages */}
        <Card className="h-fit">
          <CardContent className="space-y-4 p-3">
            <div>
              <p className="mb-1.5 px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pages</p>
              {pageArray.map((value, index) => (
                <div
                  key={value.id}
                  onClick={() => setPage(value.id)}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors",
                    index + 1 == page ? "bg-primary/10 font-medium text-primary" : "text-muted-foreground hover:bg-muted"
                  )}
                >
                  <FileText className="h-4 w-4 shrink-0" />
                  {value.title}
                </div>
              ))}
            </div>
            <div>
              <p className="mb-1.5 px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sub pages</p>
              {(getBannerMetaData || [])?.map((value: any) => (
                <div
                  key={value?.id}
                  onClick={() => subPageAction(value?.id)}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors",
                    value?.id == subPage ? "bg-primary/10 font-medium text-primary" : "text-muted-foreground hover:bg-muted"
                  )}
                >
                  <ImageIcon className="h-4 w-4 shrink-0" />
                  {value?.name}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Image list */}
        <div>
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <ImageIcon className="h-5 w-5" />
              </div>
              <div>
                <h1 className="text-base font-semibold text-foreground">Image list</h1>
                <p className="text-sm text-muted-foreground">Manage images for the selected sub page.</p>
              </div>
            </div>
            <Button onClick={() => showDrawer("")}>
              <Plus className="mr-1.5 h-4 w-4" /> Add Image
            </Button>
          </div>
{/* 
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Search images…"
                value={imageSearch}
                onChange={(e) => setImageSearch(e.target.value)}
              />
            </div>
            <Select value={imageSort} onValueChange={(v) => setImageSort(v as typeof imageSort)}>
              <SelectTrigger className="w-full sm:w-[190px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sortOrder">Sort by: Sort Order</SelectItem>
                <SelectItem value="name">Sort by: Name</SelectItem>
                <SelectItem value="date">Sort by: Start Date</SelectItem>
              </SelectContent>
            </Select>
          </div> */}

          {isArrayNotEmpty(displayedBannerList) ? (
            <div className="overflow-hidden rounded-xl border">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Preview</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Date Range</TableHead>
                    <TableHead>Sort Order</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(displayedBannerList || [])?.map((value: any, index: number) => (
                    <TableRow key={index}>
                      <TableCell>
                        <img src={`${IMG_BASE}${value?.fileUuid}`} alt="" className="h-12 w-16 rounded-lg border object-cover" />
                      </TableCell>
                      <TableCell>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="block max-w-[160px] truncate font-medium">{value?.fileName}</span>
                            </TooltipTrigger>
                            <TooltipContent>{value?.fileName}</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                          <Calendar className="h-3.5 w-3.5 shrink-0" />
                          {moment(value?.startDate).format("DD-MMM-YYYY") + " to " + moment(value?.endDate).format("DD-MMM-YYYY")}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-md bg-primary/10 px-1.5 text-xs font-semibold tabular-nums text-primary">
                          {value?.sortOrder}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <Button variant="outline" size="icon" aria-label="Edit" onClick={() => showDrawer(value)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            aria-label="Delete"
                            className="border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
                            onClick={() => showModal(value)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="rounded-xl border"><EmptyState /></div>
          )}
        </div>
      </div>

      {/* Add / Edit drawer */}
      <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
          <SheetHeader className="border-b px-5 py-4">
            <SheetTitle>{isEdit ? "Edit Banner" : "Add Banner"}</SheetTitle>
          </SheetHeader>
          <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-5">
            <div className="space-y-2">
              <Label>Image <span className="text-destructive">*</span></Label>
              <ImageDropArea onPick={onPickImage} />
              {imageError && <p className="text-xs text-destructive">{imageError}</p>}
              <div className="flex flex-wrap gap-3">
                {isArrayNotEmpty(imgList) ? imgList?.map((item: any, index: any) => (
                  <div key={index} className="relative h-24 w-24 overflow-hidden rounded-lg border">
                    {!item?.bannerId
                      ? <img src={item?.previewImage} alt="welcome" className="h-full w-full object-cover" />
                      : <img src={`${IMG_BASE}${item?.fileUuid}`} alt="welcome" className="h-full w-full object-cover" />}
                    <button
                      type="button"
                      onClick={() => setImages(item)}
                      aria-label="Remove image"
                      className="absolute right-1 top-1 rounded-md bg-background/80 p-0.5 text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )) : null}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Sort Order <span className="text-destructive">*</span></Label>
              <Select value={String(bannerSortOrder)} onValueChange={(v) => setBannerSortOrder(Number(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4].map((item) => (
                    <SelectItem key={item} value={String(item)}>{item}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Start Date to End Date <span className="text-destructive">*</span></Label>
              <DateRangePicker value={rangeValue} onChange={handleRangeChange} disableFuture={false} placeholder="Start Date – End Date" />
              {rangeError && <p className="text-xs text-destructive">{rangeError}</p>}
            </div>

            <div className="mt-auto flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button disabled={createOrUpdateBannerLoad} onClick={uploadBannerSubmit}>Upload</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete confirm */}
      <ConfirmDialog
        open={isModalOpen}
        onOpenChange={(o) => { if (!o) handleCancel(); }}
        title={`Are you sure to Delete this ${deleteBannerData?.fileName} Image?`}
        confirmLabel="Delete"
        destructive
        loading={createOrUpdateBannerLoad}
        onConfirm={() => deleteData(deleteBannerData)}
      />

      <LoadingOverlay loading={createOrUpdateBannerLoad || getBannerAttachmentLoad} />
    </div>
  )
}

interface ImageDropAreaProps {
  onPick: (file: File) => void;
}

function ImageDropArea({ onPick }: ImageDropAreaProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const emit = (list: FileList | null) => {
    if (!list) return;
    Array.from(list).forEach((f) => onPick(f));
  };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => inputRef.current?.click()}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") inputRef.current?.click(); }}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); emit(e.dataTransfer.files); }}
      className={cn(
        "flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed p-6 text-center outline-none transition-colors",
        "hover:border-primary/60 focus-visible:ring-2 focus-visible:ring-ring",
        dragging ? "border-primary bg-primary/5" : "border-border"
      )}
    >
      <UploadCloud className="h-6 w-6 text-muted-foreground" />
      <span className="text-sm text-foreground">
        Drag &amp; Drop file here or <span className="text-primary underline">Browse</span>
      </span>
      <input
        ref={inputRef}
        type="file"
        accept=".png,.jpg,.jpeg"
        className="hidden"
        onChange={(e) => { emit(e.target.files); e.target.value = ""; }}
      />
    </div>
  );
}

export default Banner
